import React, { useEffect, useContext, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { highlighter } from '../../tools/searchFilter';
import SortIcon from './../ui-components/SortIcon';
import SETTING_KEYS from '../../constants/setting-keys';
import styles from './Results.module.css';
import AuthContext, { StoredContext } from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import DatapipeLink from '../general/DatapipeLink';
import {
    getDatapipeImplantEditUrl,
    getDatapipeImplantViewUrl,
} from '../../utils/datapipeLinks';
import Button, { ButtonType } from '../general-components/Button';
import Modal from '../manage-user/Modal';
import { archiveImplant } from '../../services/apiService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import Status from '../general-components/Status';
import { Search } from 'lucide-react';

const Results: React.FC<{
    title?: string;
    term?: string;
    defaultResults: any[];
    isManufacturer?: boolean;
    isDept?: boolean;
    editStatus?: number;
    onChange?: Function;
    hasSearch?: boolean;
}> = ({
    title,
    term,
    defaultResults,
    isManufacturer = false,
    isDept = false,
    editStatus = -1,
    onChange,
    hasSearch = false,
}) => {
    const { t } = useTranslation();
    const userContext = useContext(AuthContext);

    const [results, setResults] = useState<any[]>([]);
    const [sortStatus, setSortStatus] = useState(['none', '']);
    const [archiveId, setArchiveId] = useState('');
    const inputRef = useRef(null);
    const timeoutRef = useRef(null);

    const [smallScreen, setSmallScreen] = useState(false);
    //Search functionality
    const [isSearchVisible, setSearchVisible] = useState(false);

    const openSearch = () => {
        setSearchVisible(true);
    };

    const closeSearch = () => {
        setSearchVisible(false);
    };

    useEffect(() => {
        sortResults(sortStatus[0]);
    }, [sortStatus, defaultResults]);

    const sortResults = (v: string) => {
        let arr = [];

        if (sortStatus[1] === 'asc') {
            arr = ([] as any[]).concat(results).sort((x, y) => {
                if (x[v] < y[v] || x.info[v] < y.info[v]) {
                    return -1;
                }
                if (x[v] > y[v] || x.info[v] > y.info[v]) {
                    return 1;
                }
                return 0;
            });
        } else if (sortStatus[1] === 'desc') {
            arr = ([] as any[]).concat(results).sort((x, y) => {
                if (x[v] < y[v] || x.info[v] < y.info[v]) {
                    return 1;
                }
                if (x[v] > y[v] || x.info[v] > y.info[v]) {
                    return -1;
                }
                return 0;
            });
        } else {
            arr = defaultResults;
        }
        setResults(arr);
    };

    const sort = (event: React.MouseEvent) => {
        let parent = event.target as HTMLElement;
        while (parent.tagName !== 'TH') {
            parent = parent.parentElement as HTMLElement;
        }
        const v = parent.id;
        parent.style['fontWeight'] = 'bold';
        if (sortStatus[0] !== 'none' && sortStatus[0] !== v) {
            (document.getElementById(sortStatus[0]) as HTMLElement).style[
                'fontWeight'
            ] = 'normal';
        }

        if (sortStatus[0] === v) {
            if (sortStatus[1] === '') {
                setSortStatus([v, 'asc']);
            } else if (sortStatus[1] === 'asc') {
                setSortStatus([v, 'desc']);
            } else if (sortStatus[1] === 'desc') {
                (document.getElementById(sortStatus[0]) as HTMLElement).style[
                    'fontWeight'
                ] = 'normal';
                setSortStatus(['none', '']);
            }
        } else {
            setSortStatus([v, 'asc']);
        }
    };

    const handleBlur = () => {
        timeoutRef.current = setTimeout(() => {
            setSearchVisible(false);
        }, 5000);
    };
    const handleFocus = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    useEffect(() => {
        if (onChange && archiveId === '') {
            onChange();
        }
    }, [archiveId]);

    useEffect(() => {
        const handler = (e: any) => {
            console.log(e.matches);
            setSmallScreen(e.matches);
        };
        window
            .matchMedia('(max-width: 1400px)')
            .addEventListener('change', handler);
    }, []);
    useEffect(() => {
        if (isSearchVisible) {
            inputRef.current?.focus(); // Focus the input element
        }
    }, [isSearchVisible]);

    return (
        <div className={styles.results}>
            <div className="flex flex-row justify-between items-center">
                {title && <h2>{title}</h2>}
                {hasSearch && (
                    <div className="flex items-center justify-center">
                        {/* Search Container */}
                        {isSearchVisible ? (
                            <div
                                className={`border border-gray-300 rounded-3xl flex flex-row justify-center items-center px-3 
          transition-transform transform
          animate-[fadeIn_0.3s_ease-out,scaleUp_0.3s_ease-out]`}
                            >
                                <input
                                    onBlur={handleBlur}
                                    onFocus={handleFocus}
                                    ref={inputRef}
                                    type="text"
                                    className="bg-transparent text-white text-2xl py-0 px-3"
                                    placeholder={t(
                                        'search.results.search.placeholder'
                                    )}
                                />
                                <Search />
                            </div>
                        ) : (
                            <Search
                                onClick={openSearch}
                                className="animate-[fadeIn_0.3s_ease-out] text-white"
                            />
                        )}
                    </div>
                )}
            </div>

            <table
                className={`${styles.list} rounded-2xl overflow-hidden border border-gray-300 ease-in-out duration-150`}
            >
                <thead>
                    <tr>
                        <th id={SETTING_KEYS.model} onClick={(e) => sort(e)}>
                            {t('search.results.table.headers.model')}{' '}
                            <SortIcon
                                sortStatus={sortStatus}
                                headerId={SETTING_KEYS.model}
                            />
                        </th>
                        {!isManufacturer && (
                            <th
                                id={SETTING_KEYS.manufacturer}
                                onClick={(e) => sort(e)}
                            >
                                {t('search.results.table.headers.brand')}{' '}
                                <SortIcon
                                    sortStatus={sortStatus}
                                    headerId={SETTING_KEYS.manufacturer}
                                />
                            </th>
                        )}
                        {!smallScreen && !isDept && (
                            <th id="_id" onClick={(e) => sort(e)}>
                                {t('search.results.table.headers.id')}{' '}
                                <SortIcon
                                    sortStatus={sortStatus}
                                    headerId="_id"
                                />
                            </th>
                        )}
                        <th id={SETTING_KEYS.type} onClick={(e) => sort(e)}>
                            {t('search.results.table.headers.type')}{' '}
                            <SortIcon
                                sortStatus={sortStatus}
                                headerId={SETTING_KEYS.type}
                            />
                        </th>
                        {isDept && (
                            <th id="_id" onClick={(e) => sort(e)}>
                                {t('search.results.table.headers.mriStatus')}
                                <SortIcon
                                    sortStatus={sortStatus}
                                    headerId="_id"
                                />
                            </th>
                        )}
                        {userContext.orRights([
                            RIGHTS_KEYS.manageAllImplants,
                            RIGHTS_KEYS.manageImplant,
                        ]) && (
                            <>
                                <th id={SETTING_KEYS.type}>
                                    {t('search.results.table.headers.edit')}{' '}
                                </th>
                            </>
                        )}
                        {[2, 3].includes(editStatus) && (
                            <>
                                <th id={SETTING_KEYS.type}>
                                    {t(
                                        'search.results.table.headers.verification'
                                    )}{' '}
                                </th>

                                <th id={SETTING_KEYS.type}>
                                    {t('search.results.table.headers.archive')}
                                </th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {results.map((imp) => (
                        <React.Fragment key={imp.info._id + '_button'}>
                            <tr
                                // onClick={() =>
                                //     (window.location.href =
                                //         '/implant/' + imp.info._id)
                                // }
                                key={imp.info._id}
                            >
                                <td>
                                    <a href={'/implant/' + imp.info._id}>
                                        {imp.info[SETTING_KEYS.model]}
                                    </a>
                                </td>
                                {!isManufacturer && (
                                    <td>{imp[SETTING_KEYS.manufacturer]}</td>
                                )}
                                {!smallScreen && !isDept && (
                                    <td>{imp.info._id}</td>
                                )}
                                <td>{imp.info[SETTING_KEYS.type]}</td>
                                {isDept && (
                                    <td>
                                        {
                                            <Status
                                                status={
                                                    imp.info[
                                                        SETTING_KEYS.mriStatus
                                                    ]
                                                }
                                            />
                                        }
                                    </td>
                                )}
                                {userContext.orRights([
                                    RIGHTS_KEYS.manageAllImplants,
                                    RIGHTS_KEYS.manageImplant,
                                ]) && (
                                    <td>
                                        <DatapipeLink
                                            href={getDatapipeImplantEditUrl(
                                                imp.info._id
                                            )}
                                            label={t(
                                                'search.results.buttons.edit'
                                            )}
                                        />
                                    </td>
                                )}
                                {[2, 3].includes(editStatus) && (
                                    <td>
                                        {userContext.orRights([
                                            RIGHTS_KEYS.manageImplant,
                                        ]) &&
                                            imp.info[
                                                SETTING_KEYS
                                                    .verifiedByManufacturer
                                            ] &&
                                            userContext.orRights([
                                                RIGHTS_KEYS.manageAllImplants,
                                            ]) &&
                                            imp.info[
                                                SETTING_KEYS.verifiedByNI
                                            ] && (
                                                <span
                                                    style={{ color: 'green' }}
                                                >
                                                    {t(
                                                        'search.results.verification.verified'
                                                    )}
                                                </span>
                                            )}
                                        {userContext.orRights([
                                            RIGHTS_KEYS.manageImplant,
                                        ]) &&
                                            imp.info[
                                                SETTING_KEYS
                                                    .verifiedByManufacturer
                                            ] &&
                                            !imp.info[
                                                SETTING_KEYS.verifiedByNI
                                            ] && (
                                                <span
                                                    style={{ color: 'orange' }}
                                                >
                                                    {t(
                                                        'search.results.verification.awaitingNI'
                                                    )}
                                                </span>
                                            )}
                                        {imp.info[
                                            SETTING_KEYS.verifiedByManufacturer
                                        ] &&
                                            imp.info[
                                                SETTING_KEYS.verifiedByNI
                                            ] && (
                                                <span style={{ color: '#4d5' }}>
                                                    {t(
                                                        'search.results.verification.verified'
                                                    )}
                                                </span>
                                            )}
                                        {userContext.orRights([
                                            RIGHTS_KEYS.manageAllImplants,
                                        ]) &&
                                            imp.info[
                                                SETTING_KEYS.verifiedByNI
                                            ] &&
                                            !imp.info[
                                                SETTING_KEYS
                                                    .verifiedByManufacturer
                                            ] && (
                                                <span
                                                    style={{ color: 'orange' }}
                                                >
                                                    {t(
                                                        'search.results.verification.awaitingManufacturer'
                                                    )}
                                                </span>
                                            )}
                                        {((userContext.orRights([
                                            RIGHTS_KEYS.manageImplant,
                                        ]) &&
                                            !imp.info[
                                                SETTING_KEYS
                                                    .verifiedByManufacturer
                                            ]) ||
                                            (userContext.orRights([
                                                RIGHTS_KEYS.verifyImplants,
                                            ]) &&
                                                !imp.info[
                                                    SETTING_KEYS.verifiedByNI
                                                ])) &&
                                            [2].includes(editStatus) && (
                                                <DatapipeLink
                                                    href={getDatapipeImplantViewUrl(
                                                        imp.info._id
                                                    )}
                                                    label={t(
                                                        'search.results.buttons.verify'
                                                    )}
                                                    variant="default"
                                                />
                                            )}
                                    </td>
                                )}

                                {[2, 3].includes(editStatus) && (
                                    <td
                                        className={styles.archiveButton}
                                        onClick={() => {
                                            setArchiveId(imp.info._id);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            color={'#f55'}
                                        />
                                    </td>
                                )}
                            </tr>
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            {archiveId.length > 0 &&
                userContext.orRights([
                    RIGHTS_KEYS.manageImplant,
                    RIGHTS_KEYS.manageAllImplants,
                ]) && (
                    <Modal
                        info={{
                            title: t(
                                'search.results.modals.archiveImplant.title'
                            ),
                            message: t(
                                'search.results.modals.archiveImplant.message',
                                {
                                    model: defaultResults.filter(
                                        (i) => i.info._id === archiveId
                                    )[0].info[SETTING_KEYS.model],
                                }
                            ),
                        }}
                        updateView={() => {
                            setArchiveId('');
                        }}
                    >
                        <div className={styles.wrapper}>
                            <form>
                                <Button
                                    key={'Save'}
                                    type={ButtonType.cancel}
                                    onClick={async () => {
                                        await archiveImplant(archiveId);
                                        setArchiveId('');
                                    }}
                                >
                                    {t('search.results.buttons.archive')}
                                </Button>
                                <Button
                                    key={'Cancel'}
                                    type={ButtonType.tietary}
                                    onClick={() => {
                                        setArchiveId('');
                                    }}
                                >
                                    {t('search.results.buttons.cancel')}
                                </Button>
                            </form>
                        </div>
                    </Modal>
                )}
        </div>
    );
};

export default Results;
